import { useState, useCallback, useEffect } from 'react';
import {
  getAccessToken,
  isTokenValid,
  getUserDataFromToken,
  saveUserData,
  clearTokens,
  type UserData,
} from '../../services/auth';
import { restoreSession, refreshSession } from '../../services/auth';

const AUTH_URL = process.env.NEXT_PUBLIC_BASE_URL || '';

export interface AuthStateResult {
  user: UserData | null;
  loading: boolean;
  setUser: (user: UserData | null) => void;
  setLoading: (loading: boolean) => void;
  isAuthenticated: () => boolean;
  refreshUserData: (options?: RefreshOptions) => Promise<boolean>;
  updateUserData: (data: Partial<UserData>) => void;
  clearAuthData: () => void;
}

interface RefreshOptions {
  maxRetries?: number;
  retryDelay?: number;
  forceTokenRefresh?: boolean;
}

export function useAuthState(): AuthStateResult {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  const clearAuthData = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  const isAuthenticated = useCallback(() => {
    if (typeof window === 'undefined') return false;
    const token = getAccessToken();
    return !!user && !!token && isTokenValid();
  }, [user]);

  const refreshUserData = useCallback(
    async (options: RefreshOptions = {}): Promise<boolean> => {
      const { maxRetries = 3, retryDelay = 1000, forceTokenRefresh = true } = options;

      const attemptRefresh = async (attempt = 1): Promise<boolean> => {
        try {
          let currentToken = getAccessToken();
          if (!currentToken || !isTokenValid()) return false;

          if (attempt === 1 && forceTokenRefresh) {
            const refreshResult = await refreshSession();
            if (refreshResult.success && refreshResult.accessToken) {
              currentToken = refreshResult.accessToken;
            }
          }

          const response = await fetch(`${AUTH_URL}/api/auth/user`, {
            headers: { Authorization: `Bearer ${currentToken}` },
          });

          if (response.ok) {
            const data = await response.json();
            if (data) {
              const freshUserData = getUserDataFromToken(currentToken);

              if (freshUserData) {
                const freshFirstName = data.user_metadata?.first_name || '';
                const freshLastName = data.user_metadata?.last_name || '';
                const freshFullName =
                  freshFirstName && freshLastName
                    ? `${freshFirstName} ${freshLastName}`.trim()
                    : data.user_metadata?.full_name || freshUserData.fullName;

                const updatedUserData: UserData = {
                  ...freshUserData,
                  username: data.user_metadata?.user_name || freshUserData.username,
                  fullName: freshFullName,
                  firstName: freshFirstName,
                  lastName: freshLastName,
                  ...(data.user_metadata && {
                    user_metadata: { ...data.user_metadata, full_name: freshFullName, name: freshFullName },
                    profile: data.user_metadata.profile || freshUserData.profile,
                  }),
                  isEarthIdVerified: data.user_metadata?.earthid_verification?.is_verified || false,
                  earthIdData: data.user_metadata?.earthid_verification || {},
                };

                saveUserData(updatedUserData);
                setUser(updatedUserData);
                return true;
              }
            }
          } else {
            throw new Error(`API request failed: ${response.status}`);
          }
        } catch (error) {
          console.error(`Refresh attempt ${attempt} failed:`, error);
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay * attempt));
            return attemptRefresh(attempt + 1);
          }
          throw error;
        }
        return false;
      };

      try {
        return await attemptRefresh();
      } catch {
        const currentToken = getAccessToken();
        const userData = getUserDataFromToken(currentToken);
        if (userData) {
          saveUserData(userData);
          setUser(userData);
          return true;
        }
        return false;
      }
    },
    []
  );

  const updateUserData = useCallback(
    (updatedData: Partial<UserData>) => {
      if (!user) return;
      const newUserData = { ...user, ...updatedData };
      saveUserData(newUserData);
      setUser(newUserData);
    },
    [user]
  );

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      try {
        const token = getAccessToken();

        if (token && isTokenValid()) {
          const result = await restoreSession();

          if (result.success && result.userData) {
            setUser(result.userData);
          } else {
            const userData = getUserDataFromToken(token);
            if (userData) {
              saveUserData(userData);
              setUser(userData);
            } else {
              clearAuthData();
            }
          }
        } else {
          clearAuthData();
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [clearAuthData]);

  return {
    user,
    loading,
    setUser,
    setLoading,
    isAuthenticated,
    refreshUserData,
    updateUserData,
    clearAuthData,
  };
}
